import { useState } from "react";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_x9hsiqe";
const EMAILJS_TEMPLATE_ID = "template_fw4nx9u";
const EMAILJS_PUBLIC_KEY = "xl6o2FbDrP_sIatDz";

const SERVICES = [
  "Airport Transfer",
  "Day Trip",
  "Tour",
  "Other",
];

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-3xl transition-transform hover:scale-110 focus:outline-none"
        >
          <span className={(hovered || value) >= star ? "text-amber-400" : "text-gray-300"}>
            ★
          </span>
        </button>
      ))}
    </div>
  );
}

function FeedbackSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    tripDate: "",
    rating: 0,
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) {
      alert("Please select a star rating.");
      return;
    }

    setLoading(true);
    const stars = "★".repeat(formData.rating) + "☆".repeat(5 - formData.rating);
    const templateParams = {
      name: formData.name,
      email: formData.email,
      message:
        `FEEDBACK\n` +
        `Service: ${formData.service}\n` +
        `Trip Date: ${formData.tripDate || "Not specified"}\n` +
        `Rating: ${stars} (${formData.rating}/5)\n\n` +
        `${formData.message}`,
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setSubmitted(true);
    } catch (error) {
      console.error("EmailJS error:", error);
      alert("Failed to send feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-3xl bg-white/70 backdrop-blur border border-white/40 shadow-xl ring-1 ring-black/5">
      <div className="px-6 py-8 sm:px-10 sm:py-12">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-widest text-blue-700/70 font-semibold">Share Your Experience</p>
          <h2 className="mt-1 text-3xl sm:text-4xl font-bold text-gray-900 font-poppins">Leave Feedback</h2>
          <p className="mt-2 text-gray-600">We'd love to hear how your trip went.</p>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-5xl mb-4">🙏</div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Thank you for your feedback!</h3>
            <p className="text-gray-600">We really appreciate you taking the time to share your experience.</p>
            <button
              onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", service: "", tripDate: "", rating: 0, message: "" }); }}
              className="mt-6 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
              />
            </div>

            {/* Service */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Service Used</label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Trip Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Trip</label>
              <input
                type="date"
                name="tripDate"
                value={formData.tripDate}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Rating</label>
              <StarRating value={formData.rating} onChange={(r) => setFormData((prev) => ({ ...prev, rating: r }))} />
            </div>

            {/* Message */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Experience</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about your experience — what did you enjoy, and is there anything we could improve?"
                required
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white/80 backdrop-blur transition-all duration-200"
              />
            </div>

            {/* Submit */}
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4 text-white font-semibold text-lg shadow-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600/40 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Submit Feedback"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

export default FeedbackSection;
